/**
 * Session handling.
 *
 * The customer app runs on anonymous sessions: Supabase issues a real JWT with
 * a real `auth.uid()` without asking for an email address, which is what lets
 * the order tables use ordinary RLS instead of a bypass. The merchant app uses
 * ordinary email and password accounts.
 */

import type { Session, User } from '@supabase/supabase-js';

import { ApiError, unwrap, type DbClient } from './client';

/**
 * The current session, if any.
 *
 * @param client A Supabase client.
 * @returns The active session, or null.
 */
export async function getSession(client: DbClient): Promise<Session | null> {
  const { data } = await client.auth.getSession();
  return data.session ?? null;
}

/**
 * Returns the current session, creating an anonymous one when absent.
 *
 * Safe to call on every app boot: it is a no-op once a session exists, so a
 * customer's order history survives a reload.
 *
 * @param client A Supabase client.
 * @returns The active session.
 * @throws ApiError when anonymous sign-in is disabled on the project.
 */
export async function ensureAnonymousSession(client: DbClient): Promise<Session> {
  const existing = await getSession(client);
  if (existing) return existing;

  const { data, error } = await client.auth.signInAnonymously();
  if (error) {
    throw new ApiError(
      `${error.message} — check that anonymous sign-ins are enabled for this Supabase project.`,
      error.status?.toString(),
    );
  }
  if (!data.session) {
    throw new ApiError('Anonymous sign-in returned no session');
  }
  return data.session;
}

/**
 * Creates a merchant account.
 *
 * @param client A Supabase client.
 * @param email Account email.
 * @param password Account password, already validated client-side.
 * @param redirectTo Where the confirmation link should return the merchant.
 * @returns The new user, and whether email confirmation still blocks sign-in.
 */
export async function signUpWithPassword(
  client: DbClient,
  email: string,
  password: string,
  redirectTo?: string,
): Promise<{ user: User | null; needsEmailConfirmation: boolean }> {
  const { data, error } = await client.auth.signUp({
    email: email.trim(),
    password,
    // Without this the confirmation link is built from the project's `site_url`,
    // which is a bare origin that need not be either app — so the merchant
    // confirms and lands on nothing.
    ...(redirectTo ? { options: { emailRedirectTo: redirectTo } } : {}),
  });
  if (error) throw new ApiError(error.message, error.status?.toString());

  return {
    user: data.user,
    // With confirmation enabled Supabase returns a user but no session.
    needsEmailConfirmation: data.user !== null && data.session === null,
  };
}

/**
 * Signs a merchant in.
 *
 * @param client A Supabase client.
 * @param email Account email.
 * @param password Account password.
 * @returns The authenticated session.
 */
export async function signInWithPassword(
  client: DbClient,
  email: string,
  password: string,
): Promise<Session> {
  const { data, error } = await client.auth.signInWithPassword({ email: email.trim(), password });
  if (error) throw new ApiError(error.message, error.status?.toString());
  if (!data.session) throw new ApiError('Sign-in returned no session');
  return data.session;
}

/**
 * Starts a Google sign-in.
 *
 * The call navigates the browser to Google and does not resolve to a session:
 * the merchant comes back to `redirectTo` with the session in the URL, which is
 * why the client that runs this must have `detectSessionInUrl` enabled. An error
 * here means the redirect could not be started — most often because the Google
 * provider is not enabled on the project yet.
 *
 * @param client A Supabase client.
 * @param redirectTo Where the provider should return the merchant.
 * @throws ApiError when the redirect could not be started.
 */
export async function signInWithGoogle(client: DbClient, redirectTo: string): Promise<void> {
  const { error } = await client.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo,
      // Let the merchant choose rather than silently reusing whichever Google
      // account the browser is already signed into. A counter iPad shared
      // between staff is exactly where that would pick the wrong one.
      queryParams: { prompt: 'select_account' },
    },
  });
  if (error) throw new ApiError(error.message, error.status?.toString());
}

/**
 * Which of an account's sessions a sign-out ends.
 *
 * `local` ends the session in this browser and leaves every other device signed
 * in. `global` revokes the account's refresh tokens for every session, so the
 * others drop out as they next refresh — the answer for a device that has gone
 * missing, and it ends this one too.
 */
export type SignOutScope = 'local' | 'global';

/**
 * Ends a session, on this device or on all of them.
 *
 * `local` is the default, and the opposite of what `supabase-js` does when it is
 * not told: its own default scope is `global`, so a Sign out button that passes
 * nothing signs the merchant out of the tablet at the counter as well.
 *
 * @param client A Supabase client.
 * @param scope Which sessions to end. Defaults to this device only.
 */
export async function signOut(client: DbClient, scope: SignOutScope = 'local'): Promise<void> {
  const { error } = await client.auth.signOut({ scope });
  if (error) throw new ApiError(error.message, error.status?.toString());
}

/**
 * Subscribes to auth state changes.
 *
 * @param client A Supabase client.
 * @param listener Called with the new session on every change.
 * @returns An unsubscribe function.
 */
export function onAuthStateChange(
  client: DbClient,
  listener: (session: Session | null) => void,
): () => void {
  const { data } = client.auth.onAuthStateChange((_event, session) => {
    listener(session);
  });
  return () => data.subscription.unsubscribe();
}

/**
 * Records the customer's phone number on their anonymous account so the same
 * device is recognisable across orders.
 *
 * Best-effort: the order carries the authoritative contact details regardless,
 * so a failure here must not block checkout.
 *
 * @param client A Supabase client.
 * @param phone Normalised phone number.
 * @param name Optional display name.
 */
export async function rememberContact(
  client: DbClient,
  phone: string,
  name?: string,
): Promise<void> {
  try {
    await client.auth.updateUser({ data: { phone, ...(name ? { name } : {}) } });
  } catch {
    // Contact metadata is a convenience; the order row is the record of truth.
  }
}

/**
 * Reads contact details previously stored on the anonymous account.
 *
 * @param client A Supabase client.
 * @returns Stored name and phone, when present.
 */
export async function recallContact(client: DbClient): Promise<{ name?: string; phone?: string }> {
  const { data } = await client.auth.getUser();
  const meta = (data.user?.user_metadata ?? {}) as { name?: string; phone?: string };
  return { ...(meta.name ? { name: meta.name } : {}), ...(meta.phone ? { phone: meta.phone } : {}) };
}

// Re-exported so callers do not need a direct dependency on supabase-js types.
export type { Session, User };
export { unwrap };
